import { useHeaderAtom } from "@RHCommerceDev/hooks/atoms/useHeaderAtom";
import useDidMountEffect from "@RHCommerceDev/hooks/useDidMountEffect";
import useGetNavigation from "@RHCommerceDev/hooks/useGetNavigation";
import { useLocalization } from "@RHCommerceDev/hooks/useLocalization";
import useLocale from "@RHCommerceDev/hooks/useLocale";
import { cn } from "@RHCommerceDev/utils/cn";
import React from "react";
import { Typography } from "@RHCommerceDev/unstyled-ui-components";
import parsedCatalogNavigation from "utils/parseCatalogNavigationData";
import NavigationDropdown from "./catalogNavigationDropdown";
import memoize from "utils/memoize";
import useScrollLock from "@RHCommerceDev/hooks/useScrollLock";
import { useCountry } from "@RHCommerceDev/hooks/useCountry";
import { useAppId } from "hooks/useAppId";
import getLocalizedUrl from "@RHCommerceDev/utils/getLocalizedUrl";
import he from "he";

const CatalogNavigationV2 = () => {
  const {
    catalogNavigation,
    saleCatalogNavigation,
    refetchCatalogNavigation,
    refetchSaleCatalogNavigation
  } = useGetNavigation();
  const locale = useLocalization();
  const splitLocale = useLocale();
  const countryCode = useCountry();
  const { isConcierge } = useAppId();

  const navigationData = parsedCatalogNavigation(
    catalogNavigation,
    saleCatalogNavigation,
    isConcierge
  );
  const categoryArray = navigationData?.childCategories;

  const isFrenchMontreal = splitLocale === "fr-CA" || splitLocale === "fr-FR";
  const {
    state,
    closeCategoryDropdown,
    openCategoryDropdown,
    setSelectedCategoryIndex
  } = useHeaderAtom();

  useDidMountEffect(() => {
    refetchCatalogNavigation();
    refetchSaleCatalogNavigation();
  }, [locale, countryCode]);

  //lock screen when category dropdown is open
  useScrollLock(state.isCategoryDropdownOpen);

  return (
    <>
      {/* Navigation is hidden below md */}
      <div
        className={cn("hidden w-full flex-nowrap justify-between p-0 md:flex", {
          "px-10 pb-4": isConcierge
        })}
        onClick={e => e.stopPropagation()}
      >
        {categoryArray?.map((category, index) =>
          category?.childCategories === null ? (
            <a
              key={index}
              href={getLocalizedUrl(
                category?.targetUrl ?? "",
                locale,
                isConcierge
              )}
              className="flex items-end text-inherit no-underline"
              target="_blank"
              onClick={e => {
                const target = e?.target as HTMLElement;
                const { id, className } = target;
                document?.body?.dispatchEvent(
                  new CustomEvent("cta_click", {
                    detail: {
                      item: {
                        isHeaderNavigationNew: true,
                        topCat: category?.displayName,
                        parentCat: category?.targetUrl,
                        cat: category?.targetUrl,
                        catURL: category?.targetUrl,
                        class: className,
                        id: id
                      }
                    }
                  })
                );
              }}
            >
              <Typography
                variant="default"
                fontFamily="rhc"
                fontWeight="thin"
                className={cn(
                  "text-[13px] uppercase leading-[1.5em] tracking-[0.05em] lg:text-base",
                  {
                    "opacity-30":
                      state.selectedCategoryIndex !== -1 &&
                      index !== state.selectedCategoryIndex,
                    "tracking-[5%] md:text-[11px] lg:text-[12.5px] xl:text-[14px]":
                      isFrenchMontreal
                  }
                )}
                dangerouslySetInnerHTML={{
                  __html: he.decode(category?.displayName ?? "")
                }}
              />
            </a>
          ) : (
            <button
              key={index}
              onClick={e => {
                e.stopPropagation();
                if (index === state.selectedCategoryIndex) {
                  closeCategoryDropdown();
                  setSelectedCategoryIndex(-1);
                } else {
                  openCategoryDropdown();
                  setSelectedCategoryIndex(index);
                }
              }}
              className="p-0 text-inherit"
            >
              <Typography
                variant="default"
                fontFamily="rhc"
                fontWeight="thin"
                data-analytics-rootcat={category.displayName}
                className={cn(
                  "text-[13px] uppercase leading-[1.5em] tracking-[0.05em] lg:text-base",
                  {
                    "opacity-30":
                      state.selectedCategoryIndex !== -1 &&
                      index !== state.selectedCategoryIndex,
                    "tracking-[5%] md:text-[11px] lg:text-[12.5px] xl:text-[14px]":
                      isFrenchMontreal,
                    "font-semibold tracking-[0.025em]":
                      index === state.selectedCategoryIndex
                  }
                )}
                dangerouslySetInnerHTML={{
                  __html: he.decode(category?.displayName ?? "")
                }}
              />
            </button>
          )
        )}
      </div>

      <NavigationDropdown
        key={state.selectedCategoryIndex}
        selectedCategory={categoryArray?.[state.selectedCategoryIndex]}
      />
    </>
  );
};

export default memoize(CatalogNavigationV2);
